import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PlatformTable, Tag, type PlatformPillar } from "@/components/ds";
import { useSectionHeader } from '@/hooks/useSectionHeaders';
import { RenderHeaderParts } from '@/components/ds/RenderCMSHeader';
import { useContentHover } from '@/hooks/useContentHover';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { mergeRefs } from '@/utils/mergeRefs';

/**
 * Platform Section (About page)
 * 
 * CMS Integration:
 * - Uses useSectionHeader('about', 'platform') for header/tag
 */

const pillars: PlatformPillar[] = [
  {
    number: "01",
    label: "Structural Dislocation",
    headline: "The mid-market funding gap.",
    body: "Capital flows freely at the top end of corporate credit. But in the mid-market – facilities below R1bn backed by real, physical assets – there's a structural void. Not because the assets aren't bankable, but because traditional banks have retreated from the operational intensity required to serve them.",
  },
  {
    number: "02",
    label: "The Complexity Premium",
    headline: "Yield from capability, not capital.",
    body: "Returns in this segment come from navigating real asset complexity – technical origination, cashflow-linked structuring, active portfolio management, hands-on workout capacity. For traditional allocators, building this in-house is uneconomic. For emerging managers, the barrier to execution is too high. For us, it's 35 years of accumulated infrastructure.",
  },
  {
    number: "03",
    label: "Real Asset Management",
    headline: "Operators become partners.",
    body: "We don't build capability on our balance sheet – we incubate it through our loan book. Every facility is a commercially funded test of an operator's edge. Those who prove resilient through the credit cycle evolve from borrowers into integrated partners – transforming credit risk into proprietary deal flow.",
  },
];

export default function PlatformSection() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  // CMS data
  const sectionHeader = useSectionHeader('about', 'platform');

  // ⚠️ ANALYTICS: Content hover tracking
  const { contentViewRef: platformViewRef, handleMouseEnter: platformMouseEnter, handleMouseLeave: platformMouseLeave } = useContentHover({
    contentId: 'our-platform',
    contentType: 'content',
    contentTitle: 'Our Platform',
    contentSection: 'platform',
    contentPage: 'about',
    minHoverDuration: 2000,
  });

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef } = useSectionVisibility({
    sectionId: 'platform',
    sectionName: 'Our Platform',
    pageName: 'About',
    position: 2,
  });

  return (
    <section ref={mergeRefs(sectionRef, platformViewRef)} id="platform" className="py-24 md:py-32 bg-platinum" onMouseEnter={platformMouseEnter} onMouseLeave={platformMouseLeave}>
      <div className="container mx-auto px-4 md:px-8">
        {/* molecule-section-header-with-tag */}
        <motion.div
          {...(hasMounted && { initial: { opacity: 0, y: 30 } })}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-16 md:mb-24"
        >
          {/* atom-tag-subtle */}
          {sectionHeader?.tag_text && (
            <div className="mb-6">
              <Tag variant="subtle">{sectionHeader.tag_text}</Tag>
            </div>
          )}
          {/* atom-text-section-header + atom-text-section-header-emphasis */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-4 max-w-3xl">
            <RenderHeaderParts text={sectionHeader?.header_text || 'How we invest.||**Our secret sauce.**'} />
          </h2>
          {/* atom-text-section-subheader */}
          {sectionHeader?.sub_header && (
            <p className="text-17 text-muted-foreground max-w-2xl">
              {sectionHeader.sub_header}
            </p>
          )}
        </motion.div>

        {/* component-table-platform */}
        <PlatformTable pillars={pillars} />
      </div>
    </section>
  );
}
